import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { updatePreferences } from '../lib/api';
import { LOGO_URL, SKETCH_PATTERNS } from '../lib/images';
import MealPreferencesForm from '../components/onboarding/MealPreferencesForm';
import { ChevronLeft, SkipForward } from 'lucide-react';

export default function OnboardingPage() {
  const { deviceId, preferences, refreshPreferences, dismissOnboarding } = useAuth();
  const [params] = useSearchParams();
  const isEdit = params.get('edit') === 'true';
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async ({ dietary, cuisines, allergens }) => {
    setSaving(true);
    setError('');
    try {
      await updatePreferences({
        device_id: deviceId,
        meal_preferences: { dietary, cuisines, allergens },
      });
      localStorage.setItem(`today_meal_prefs_set_${deviceId}`, '1');
      if (dismissOnboarding) dismissOnboarding();
      if (refreshPreferences) await refreshPreferences();
      navigate(isEdit ? '/profile' : '/');
    } catch (e) {
      setError('Could not save preferences. Please try again.');
    }
    setSaving(false);
  };

  const handleSkip = () => {
    if (dismissOnboarding) dismissOnboarding();
    navigate('/');
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }} data-testid="onboarding-page">
      {/* Header strip */}
      <div className="relative py-8 px-4 sm:px-8 overflow-hidden keep-light-theme"
        style={{ background: 'linear-gradient(135deg, #F2EFE9 0%, #E5E1D8 50%, #D4CBB3 100%)' }}>
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: `url("${SKETCH_PATTERNS.leafBranch}")`, backgroundSize: '400px', backgroundPosition: 'right center', backgroundRepeat: 'no-repeat' }} />
        <div className="relative z-10 max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Today" className="w-10 h-10 rounded-full shadow-sm" />
            <div>
              <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>
                {isEdit ? 'Edit preferences' : 'Welcome to Today'}
              </p>
              <p className="text-base font-bold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
                Let's personalize your meals
              </p>
            </div>
          </div>
          {isEdit && (
            <Link to="/profile" className="text-sm flex items-center gap-1" style={{ color: 'var(--primary)' }} data-testid="back-to-profile">
              <ChevronLeft className="w-4 h-4" /> Back
            </Link>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto p-4 sm:p-8">
        <div className="rounded-2xl p-5 sm:p-8 border"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}>
          <MealPreferencesForm
            initial={isEdit ? (preferences?.meal_preferences || preferences || {}) : {}}
            onSubmit={handleSubmit}
            submitting={saving}
            submitLabel={isEdit ? 'Save changes' : 'Finish & explore Today'}
          />
          {error && (
            <p className="mt-3 text-sm text-center" style={{ color: '#DC2626' }}>{error}</p>
          )}
        </div>

        {!isEdit && (
          <div className="text-center mt-5">
            <button onClick={handleSkip} data-testid="skip-onboarding"
              className="text-sm flex items-center gap-1.5 mx-auto hover:opacity-80"
              style={{ color: 'var(--muted-fg)' }}>
              <SkipForward className="w-4 h-4" /> Skip for now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
